import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import practiceService from "../../../services/practiceService";
import patientService from "../../../services/patientService";
import { setUsePatientScheduleTargetsPreference } from "../../../utils/patientScheduleTargetPreference";
import PageLoadingSpinner from "../../../components/PageLoadingSpinner/PageLoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faHeartbeat,
  faTint,
  faWeight,
  faHistory,
  faCheck,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import "./ScheduleTargetSettings.css";




// --- Helper Functions ---
const convertTimeTo12Hour = (timeStr) => {
  if (!timeStr) return "";
  if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
  
  // Handle HH:mm or HH:mm:ss
  const parts = timeStr.split(":");
  let h = parseInt(parts[0]);
  const minutes = parts[1] || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${minutes.substring(0, 2)} ${period}`;
};

const convertTimeTo24Hour = (timeStr) => {
  if (!timeStr) return "";
  // If already HH:mm or HH:mm:ss, normalize to HH:mm
  if (timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
    const [h, m] = timeStr.split(":");
    return `${String(h).padStart(2, "0")}:${m.substring(0, 2)}`;
  }
  
  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    const parts = timeStr.trim().split(" ");
    if (parts.length < 2) return timeStr;
    const time = parts[0];
    const period = parts[1].toUpperCase();
    const [hours, minutes] = time.split(":");
    let h = parseInt(hours);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${minutes.substring(0, 2) || "00"}`;
  }
  return timeStr;
};

const normalizeNumericValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Normalize a row's `name`/`label` for matching DB rows against React defaults.
// We trim and lowercase so that case/whitespace drift between the master table
// and the hard-coded defaults doesn't silently break the merge.
const normalizeName = (value) => String(value ?? '').trim().toLowerCase();

// Build a `name -> row` lookup from an array of DB rows. Used by the load /
// reload code paths to overlay DB values on top of the fixed React-default
// list while keeping the visible row count stable.
const mapByName = (rows) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const key = normalizeName(r?.name ?? r?.label);
    if (key) map.set(key, r);
  });
  return map;
};

// Custom Time Input that displays AM/PM
const TimeInputAMPM = ({ value, onChange, className }) => {
  const timeInputRef = useRef(null);
  const displayInputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (value) {
      const formatted = convertTimeTo12Hour(value);
      setDisplayValue(formatted);
    } else {
      setDisplayValue("");
    }
  }, [value]);

  const handleDisplayClick = () => {
    if (timeInputRef.current) {
      timeInputRef.current.showPicker?.();
      timeInputRef.current.focus();
      timeInputRef.current.click();
    }
  };

  const handleTimeChange = (e) => {
    const newValue = e.target.value;
    setDisplayValue(convertTimeTo12Hour(newValue));
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="time-input-wrapper-custom" style={{ position: 'relative', width: '100%' }}>
      <input
        ref={displayInputRef}
        type="text"
        className={className}
        value={displayValue}
        readOnly
        onClick={handleDisplayClick}
        style={{
          cursor: 'pointer',
          paddingRight: '30px'
        }}
      />
      <input
        ref={timeInputRef}
        type="time"
        value={value || ""}
        onChange={handleTimeChange}
        style={{
          position: 'absolute',
          opacity: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          zIndex: 2
        }}
      />
    </div>
  );
};


const ScheduleTargetSettings = ({
  practiceId,
  patientId,
  isReadOnly = false,
  showSectionEventLogButtons = true,
  onOpenEventLog = null
}) => {
  // Show loading spinner until the initial DB fetch has populated values.
  // We only flip to the real form once `initialData` is set (i.e. fetch finished).
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [initialData, setInitialData] = useState(null);
  const isInitialLoading = loading && initialData === null;
  const navigate = useNavigate();
  const isAnySaving = Object.values(saving || {}).some(Boolean);

  const handleEventLog = (section = null) => {
    if (typeof onOpenEventLog === 'function') {
      onOpenEventLog(section);
      return;
    }
    if (isAnySaving) return;
    if (!practiceId) return;
    // Log format for Schedule is moduleId='Schedule'
    // If patientId exists, we use it as targetUserId, otherwise use placeholder 'all'
    const targetId = patientId || 'all';
    let path = `/event-logs/Schedule/${targetId}?practiceId=${practiceId}`;
    if (section) {
      path += `&section=${section}`;
    }
    navigate(path);
  };


  // Daily Schedule - using 12-hour format
  const [dailySchedule, setDailySchedule] = useState([
    { id: 1, name: "Wake-up Time", time: "06:00 AM" },
    { id: 2, name: "Breakfast Time", time: "08:00 AM" },
    { id: 3, name: "Lunch Time", time: "12:00 PM" },
    { id: 4, name: "Dinner Time", time: "06:00 PM" },
    { id: 5, name: "Bed Time", time: "10:00 PM" },
  ]);

  // Permanent BP period start times — these are fixed and cannot be edited
  const BP_PERMANENT_START_TIMES = {
    'Wake-up': '06:01 AM',
    'Morning': '08:01 AM',
    'Noon': '11:01 AM',
    'Afternoon': '02:01 PM',
    'Evening': '06:01 PM',
  };

  // Blood Pressure Periods & Targets - Fixed start times (matching image)
  const [bpPeriods, setBpPeriods] = useState([
    { id: 1, name: "Wake-up", start: "06:01 AM", end: "08:00 AM", fixed: true },
    { id: 2, name: "Morning", start: "08:01 AM", end: "11:00 AM", fixed: true },
    { id: 3, name: "Noon", start: "11:01 AM", end: "02:00 PM", fixed: true },
    { id: 4, name: "Afternoon", start: "02:01 PM", end: "06:00 PM", fixed: true },
    { id: 5, name: "Evening", start: "06:01 PM", end: "06:00 AM", fixed: true },
  ]);

  const [bpTargets, setBpTargets] = useState([
    { id: 1, label: "Wake-up Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90 },
    { id: 2, label: "Morning Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90 },
    { id: 3, label: "Noon Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90 },
    { id: 4, label: "Afternoon Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90 },
    { id: 5, label: "Evening Target Range", sysMin: 100, sysMax: 140, diaMin: 60, diaMax: 90 },
  ]);

  // Permanent BG period start times — these are fixed and cannot be edited
  const BG_PERMANENT_START_TIMES = {
    'Wake-up Time': '06:00 AM',
    'Before-Breakfast': '07:01 AM',
    'After-Breakfast': '09:01 AM',
    'Before-Lunch': '11:01 AM',
    'After-Lunch': '01:01 PM',
    'Before-Dinner': '05:01 PM',
    'After-Dinner': '07:01 PM',
    'Bedtime': '08:30 PM',
    'Midnight': '10:00 PM',
  };

  // Blood Glucose Periods & Targets - Fixed start times
  const [bgPeriods, setBgPeriods] = useState([
    { id: 1, name: "Wake-up Time", start: "06:00 AM", end: "07:00 AM", fixed: true },
    { id: 2, name: "Before-Breakfast", start: "07:01 AM", end: "09:00 AM", fixed: true },
    { id: 3, name: "After-Breakfast", start: "09:01 AM", end: "11:00 AM", fixed: true },
    { id: 4, name: "Before-Lunch", start: "11:01 AM", end: "01:00 PM", fixed: true },
    { id: 5, name: "After-Lunch", start: "01:01 PM", end: "05:00 PM", fixed: true },
    { id: 6, name: "Before-Dinner", start: "05:01 PM", end: "07:00 PM", fixed: true },
    { id: 7, name: "After-Dinner", start: "07:01 PM", end: "08:29 PM", fixed: true },
    { id: 8, name: "Bedtime", start: "08:30 PM", end: "10:00 PM", fixed: true },
    { id: 9, name: "Midnight", start: "10:00 PM", end: "05:59 AM", fixed: true },
  ]);

  const [bgTargets, setBgTargets] = useState([
    { id: 1, label: "Wake-up Target Rangeup Time", min: 60, max: 110 },
    { id: 2, label: "Before-Meal Target Range", min: 60, max: 110 },
    { id: 3, label: "After-Meal Target Range", min: 70, max: 140 },
    { id: 4, label: "Bedtime Target Range", min: 60, max: 110 },
    { id: 5, label: "Midnight Target Range", min: 60, max: 110 },
  ]);

  // Permanent Weight period start times — these are fixed and cannot be edited
  const WEIGHT_PERMANENT_START_TIMES = {
    'Morning': '06:00 AM',
    'Afternoon': '12:00 PM',
    'Evening': '06:00 PM',
  };

  // Weight & Fat Targets
  const [weightTargets, setWeightTargets] = useState({
    weightMin: 66,
    weightMax: 220,
    bodyFatMin: 14,
    bodyFatMax: 27,
    bmiUnderweight: "",
    bmiNormal: 18.5,
    bmiOverweight: 25,
    bmiObese: 30,
  });

  const [weightPeriods, setWeightPeriods] = useState([
    { id: 1, name: "Morning", start: "06:00 AM", end: "11:59 AM", fixed: true },
    { id: 2, name: "Afternoon", start: "12:00 PM", end: "05:59 PM", fixed: true },
    { id: 3, name: "Evening", start: "06:00 PM", end: "05:59 AM", fixed: true },
  ]);

  // Fetch data from backend
  useEffect(() => {
    if (!practiceId) return;

    const fetchScheduleTargets = async () => {
      try {
        setLoading(true);
        let response;
        if (patientId) {
          response = await patientService.getScheduleAndTargets(practiceId, patientId);
        } else {
          response = await practiceService.getScheduleAndTargets(practiceId);
        }
        if (response.data?.success && response.data?.data) {
          const data = response.data.data;

          // IMPORTANT: merge DB rows on top of the React-default rows (matching
          // by name). Replacing entirely with whatever the DB returned makes
          // sections "disappear" on staging: if a brand-new patient has saved
          // only Bed Time (so DB has 1 row), `setDailySchedule(dbRows)` would
          // collapse the visible list from 5 rows to 1. By keeping the fixed
          // default list as the structural skeleton and overlaying DB values,
          // the user always sees the full section and partial DB data is
          // simply reflected on the rows that have it.
          if (data.dailySchedule && data.dailySchedule.length > 0) {
            const dbByName = mapByName(data.dailySchedule);
            setDailySchedule(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                time: dbItem.time ? convertTimeTo12Hour(dbItem.time) : defaultItem.time
              };
            }));
          }
          if (data.bloodPressureTimePeriod && data.bloodPressureTimePeriod.length > 0) {
            const dbByName = mapByName(data.bloodPressureTimePeriod);
            setBpPeriods(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: BP_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            }));
          }
          if (data.bloodGlucoseTimePeriod && data.bloodGlucoseTimePeriod.length > 0) {
            const dbByName = mapByName(data.bloodGlucoseTimePeriod);
            setBgPeriods(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: BG_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            }));
          }
          if (data.bloodGlucoseTargetRange && data.bloodGlucoseTargetRange.length > 0) {
            const dbByName = mapByName(data.bloodGlucoseTargetRange);
            setBgTargets(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.label));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                min: dbItem.minimum ?? dbItem.min ?? defaultItem.min,
                max: dbItem.maximum ?? dbItem.max ?? defaultItem.max
              };
            }));
          }
          if (data.weightTimePeriod && data.weightTimePeriod.length > 0) {
            const dbByName = mapByName(data.weightTimePeriod);
            setWeightPeriods(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.name));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                start: WEIGHT_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
                end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
                fixed: true
              };
            }));
          }
          if (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0) {
            setWeightTargets(prev => ({
              ...prev,
              weightMin: normalizeNumericValue(data.weightTargetRange.weightMin, prev.weightMin),
              weightMax: normalizeNumericValue(data.weightTargetRange.weightMax, prev.weightMax),
              bodyFatMin: normalizeNumericValue(data.weightTargetRange.bodyFatMin, prev.bodyFatMin),
              bodyFatMax: normalizeNumericValue(data.weightTargetRange.bodyFatMax, prev.bodyFatMax),
              // Keep persisted value data-safe; UI still renders fixed "BMI" text.
              bmiUnderweight: data.weightTargetRange.bmiUnderweight ?? prev.bmiUnderweight,
              bmiNormal: 18.5,
              bmiOverweight: 25,
              bmiObese: 30
            }));
          }
          if (data.bloodPressureTargetRange && data.bloodPressureTargetRange.length > 0) {
            const dbByName = mapByName(data.bloodPressureTargetRange);
            setBpTargets(prev => prev.map((defaultItem) => {
              const dbItem = dbByName.get(normalizeName(defaultItem.label));
              if (!dbItem) return defaultItem;
              return {
                ...defaultItem,
                id: dbItem.id ?? defaultItem.id,
                sysMin: dbItem.systolicMin ?? dbItem.sysMin ?? defaultItem.sysMin,
                sysMax: dbItem.systolicMax ?? dbItem.sysMax ?? defaultItem.sysMax,
                diaMin: dbItem.diastolicMin ?? dbItem.diaMin ?? defaultItem.diaMin,
                diaMax: dbItem.diastolicMax ?? dbItem.diaMax ?? defaultItem.diaMax
              };
            }));
          }


          // Build the dirty-check baselines. They MUST mirror the merged React
          // state (full default list with DB rows overlaid by name) — never
          // just the raw DB array. Otherwise:
          //   - If DB returned 1 row, baseline has 1 entry, the other 4 rows
          //     in React state have no match in baseline, and the dirty check
          //     flags them as "changed" on the next save (logging fields the
          //     user never touched).
          //   - If DB returned nothing, fall back to defaults so first-time
          //     saves don't mass-flag every row.
          const buildMergedBaseline = (defaults, dbRows, mapDefaultToBaseline, mapDbToBaseline) => {
            const dbByName = mapByName(dbRows);
            return defaults.map((d) => {
              const dbItem = dbByName.get(normalizeName(d?.name ?? d?.label));
              return dbItem ? mapDbToBaseline(d, dbItem) : mapDefaultToBaseline(d);
            });
          };

          const baselineDaily = buildMergedBaseline(
            dailySchedule,
            data.dailySchedule,
            (s) => ({ id: s.id, name: s.name, time: convertTimeTo24Hour(s.time) }),
            (s, db) => ({ id: db.id ?? s.id, name: db.name ?? s.name, time: db.time })
          );

          const baselineBpPeriods = buildMergedBaseline(
            bpPeriods,
            data.bloodPressureTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineBpTargets = buildMergedBaseline(
            bpTargets,
            data.bloodPressureTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              systolicMin: normalizeNumericValue(t.sysMin, 0),
              systolicMax: normalizeNumericValue(t.sysMax, 0),
              diastolicMin: normalizeNumericValue(t.diaMin, 0),
              diastolicMax: normalizeNumericValue(t.diaMax, 0)
            }),
            (t, db) => ({
              id: db.id ?? t.id,
              name: db.name ?? t.label,
              systolicMin: normalizeNumericValue(db.systolicMin, normalizeNumericValue(t.sysMin, 0)),
              systolicMax: normalizeNumericValue(db.systolicMax, normalizeNumericValue(t.sysMax, 0)),
              diastolicMin: normalizeNumericValue(db.diastolicMin, normalizeNumericValue(t.diaMin, 0)),
              diastolicMax: normalizeNumericValue(db.diastolicMax, normalizeNumericValue(t.diaMax, 0))
            })
          );

          const baselineBgPeriods = buildMergedBaseline(
            bgPeriods,
            data.bloodGlucoseTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineBgTargets = buildMergedBaseline(
            bgTargets,
            data.bloodGlucoseTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              minimum: normalizeNumericValue(t.min, 0),
              maximum: normalizeNumericValue(t.max, 0)
            }),
            (t, db) => ({
              id: db.id ?? t.id,
              name: db.name ?? t.label,
              minimum: normalizeNumericValue(db.minimum, normalizeNumericValue(t.min, 0)),
              maximum: normalizeNumericValue(db.maximum, normalizeNumericValue(t.max, 0))
            })
          );

          const baselineWeightPeriods = buildMergedBaseline(
            weightPeriods,
            data.weightTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id,
              name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          );

          const baselineWeightTargetRange = (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0)
            ? JSON.parse(JSON.stringify(data.weightTargetRange))
            : {
                weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
                weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
                bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
                bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
                bmiUnderweight: weightTargets.bmiUnderweight || "",
                bmiNormal: 18.5,
                bmiOverweight: 25,
                bmiObese: 30
              };

          setInitialData({
            dailySchedule: baselineDaily,
            bpPeriods: baselineBpPeriods,
            bpTargets: baselineBpTargets,
            bgPeriods: baselineBgPeriods,
            bgTargets: baselineBgTargets,
            weightPeriods: baselineWeightPeriods,
            weightTargetRange: baselineWeightTargetRange
          });

        }
      } catch (error) {
        console.error("Error fetching schedule targets:", error);
        toast.error("Failed to load schedule targets");
        // Mark initialData with a non-null empty shape so the loading UI clears
        // and the user can at least see the defaults if the fetch crashed.
        setInitialData((prev) => prev ?? {
          dailySchedule: [], bpPeriods: [], bpTargets: [], bgPeriods: [],
          bgTargets: [], weightPeriods: [], weightTargetRange: {}
        });
      } finally {
        setLoading(false);
      }
    };

    fetchScheduleTargets();
  }, [practiceId, patientId]);

  // Handlers
  const updateDaily = (i, val) => {
    const copy = [...dailySchedule];
    copy[i].time = val;
    setDailySchedule(copy);
  };

  const updateBpPeriod = (i, field, val) => {
    const copy = [...bpPeriods];
    // Don't allow editing start time (it's fixed)
    if (field === 'start') return;
    copy[i][field] = val;
    setBpPeriods(copy);
  };

  const updateBpTarget = (i, field, val) => {
    const copy = [...bpTargets];
    // Allow empty string for blank input, only parse if not empty
    copy[i][field] = val === "" ? "" : (parseInt(val) || 0);
    setBpTargets(copy);
  };

  const updateBgPeriod = (i, field, val) => {
    const copy = [...bgPeriods];
    if (field === 'start') return; // Don't allow editing start time
    copy[i][field] = val;
    setBgPeriods(copy);
  };

  const updateBgTarget = (i, field, val) => {
    const copy = [...bgTargets];
    // Allow empty string for blank input, only parse if not empty
    copy[i][field] = val === "" ? "" : (parseInt(val) || 0);
    setBgTargets(copy);
  };

  const updateWeightTarget = (field, val) => {
    // Prevent changes to permanent BMI fields
    if (['bmiUnderweight', 'bmiNormal', 'bmiOverweight', 'bmiObese'].includes(field)) return;
    
    setWeightTargets(prev => ({
      ...prev,
      [field]: val === "" ? "" : normalizeNumericValue(val, 0)
    }));
  };

  const updateWeightPeriod = (i, field, val) => {
    const copy = [...weightPeriods];
    if (field === 'start') return; // Don't allow editing start time
    copy[i][field] = val;
    setWeightPeriods(copy);
  };

  // Build minimal payloads (changed rows only) for faster saves.
  //
  // We attach `_previous` to each changed row carrying the value that was on
  // screen before the edit (pulled from initialData, i.e. the last fetched/
  // reloaded baseline). The backend uses this as a fallback for the audit log
  // when the DB has no prior row for that item — e.g. brand-new patients on
  // staging where the section is being persisted for the first time. Without
  // it, "Old Data" in the event log would show "—".
  const getChangedDailySchedulePayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.dailySchedule || []).map((s) => [String(s.id), convertTimeTo24Hour(s.time)])
    );
    const initialByName = new Map(
      (initialData.dailySchedule || []).map((s) => [normalizeName(s.name), convertTimeTo24Hour(s.time)])
    );
    return dailySchedule
      .map((item) => {
        const time = convertTimeTo24Hour(item.time);
        const prevTime = initialMap.get(String(item.id)) ?? initialByName.get(normalizeName(item.name));
        return {
          id: item.id,
          name: item.name,
          time,
          _previous: { time: prevTime ?? null }
        };
      })
      .filter((item) => (item._previous?.time ?? null) !== item.time);
  };

  const getChangedBpPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.bpPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.bpPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return bpPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedBpTargetsPayload = () => {
    if (!initialData) return [];
    const buildInitial = (t) => ({
      systolicMin: normalizeNumericValue(t.systolicMin, 0),
      systolicMax: normalizeNumericValue(t.systolicMax, 0),
      diastolicMin: normalizeNumericValue(t.diastolicMin, 0),
      diastolicMax: normalizeNumericValue(t.diastolicMax, 0)
    });
    const initialMap = new Map(
      (initialData.bpTargets || []).map((t) => [String(t.id), buildInitial(t)])
    );
    const initialByName = new Map(
      (initialData.bpTargets || []).map((t) => [normalizeName(t.name), buildInitial(t)])
    );
    return bpTargets
      .map((t) => {
        const current = {
          systolicMin: normalizeNumericValue(t.sysMin, 0),
          systolicMax: normalizeNumericValue(t.sysMax, 0),
          diastolicMin: normalizeNumericValue(t.diaMin, 0),
          diastolicMax: normalizeNumericValue(t.diaMax, 0)
        };
        const prev = initialMap.get(String(t.id)) ?? initialByName.get(normalizeName(t.label)) ?? null;
        return {
          id: t.id,
          name: t.label,
          ...current,
          _previous: prev
        };
      })
      .filter((t) => {
        const prev = t._previous;
        if (!prev) return true;
        return (
          prev.systolicMin !== t.systolicMin ||
          prev.systolicMax !== t.systolicMax ||
          prev.diastolicMin !== t.diastolicMin ||
          prev.diastolicMax !== t.diastolicMax
        );
      });
  };

  const getChangedBgPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.bgPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.bgPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return bgPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedBgTargetsPayload = () => {
    if (!initialData) return [];
    const buildInitial = (t) => ({
      minimum: normalizeNumericValue(t.minimum, 0),
      maximum: normalizeNumericValue(t.maximum, 0)
    });
    const initialMap = new Map(
      (initialData.bgTargets || []).map((t) => [String(t.id), buildInitial(t)])
    );
    const initialByName = new Map(
      (initialData.bgTargets || []).map((t) => [normalizeName(t.name), buildInitial(t)])
    );
    return bgTargets
      .map((t) => {
        const current = {
          minimum: normalizeNumericValue(t.min, 0),
          maximum: normalizeNumericValue(t.max, 0)
        };
        const prev = initialMap.get(String(t.id)) ?? initialByName.get(normalizeName(t.label)) ?? null;
        return {
          id: t.id,
          name: t.label,
          ...current,
          _previous: prev
        };
      })
      .filter((t) => {
        const prev = t._previous;
        if (!prev) return true;
        return (
          prev.minimum !== t.minimum ||
          prev.maximum !== t.maximum
        );
      });
  };

  const getChangedWeightPeriodsPayload = () => {
    if (!initialData) return [];
    const initialMap = new Map(
      (initialData.weightPeriods || []).map((p) => [
        String(p.id),
        { name: p.name, endTime: convertTimeTo24Hour(p.endTime || p.end) }
      ])
    );
    const initialByName = new Map(
      (initialData.weightPeriods || []).map((p) => [normalizeName(p.name), convertTimeTo24Hour(p.endTime || p.end)])
    );
    return weightPeriods
      .map((p) => {
        const endTime = convertTimeTo24Hour(p.end);
        const prevEnd = initialMap.get(String(p.id))?.endTime ?? initialByName.get(normalizeName(p.name));
        return {
          id: p.id,
          name: p.name,
          startTime: convertTimeTo24Hour(p.start),
          endTime,
          _previous: { endTime: prevEnd ?? null }
        };
      })
      .filter((p) => (p._previous?.endTime ?? null) !== p.endTime);
  };

  const getChangedWeightTargetsPayload = () => {
    if (!initialData) return null;
    const current = {
      weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
      weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
      bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
      bmiUnderweight: weightTargets.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };
    const initial = {
      weightMin: normalizeNumericValue(initialData.weightTargetRange?.weightMin, 66),
      weightMax: normalizeNumericValue(initialData.weightTargetRange?.weightMax, 220),
      bodyFatMin: normalizeNumericValue(initialData.weightTargetRange?.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(initialData.weightTargetRange?.bodyFatMax, 27),
      bmiUnderweight: initialData.weightTargetRange?.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };
    if (deepEqual(current, initial)) return null;
    // Tag the previous (UI-displayed) values so the audit log can fall back to
    // them when the DB has no prior weight_target row for this user.
    return { ...current, _previous: initial };
  };

  // Save functions
  const saveDailySchedule = async () => {
    if (isReadOnly) {
      toast.warning("Practice is locked. Schedule and targets are view-only.");
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    try {
      setSaving(prev => ({ ...prev, daily: true }));
      const changedDaily = getChangedDailySchedulePayload();
      if (changedDaily.length === 0) {
        toast.info("No daily schedule changes to save");
        return;
      }
      const dataToSave = { dailySchedule: changedDaily };

      let response;
      if (patientId) {
        response = await patientService.updateScheduleAndTargets(practiceId, patientId, dataToSave);
      } else {
        response = await practiceService.updateScheduleAndTargets(practiceId, dataToSave);
      }
      if (response.data?.success) {
        if (patientId) {
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
        }
        toast.success("Daily schedule saved successfully");
        // Refetch from server so React state + initialData reflect the actual
        // DB rows (and their auto-increment IDs). Untouched sections stay
        // visible because they're pulled back from the DB, not cleared.
        await reloadFromServer();
      } else {

        throw new Error(response.data?.message || "Save failed");
      }
    } catch (error) {
      console.error("Error saving daily schedule:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to save daily schedule");
    } finally {
      setSaving(prev => ({ ...prev, daily: false }));
    }
  };

  const saveBloodPressure = async () => {
    if (isReadOnly) {
      toast.warning("Practice is locked. Schedule and targets are view-only.");
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    try {
      setSaving(prev => ({ ...prev, bp: true }));
      const changedPeriods = getChangedBpPeriodsPayload();
      const changedTargets = getChangedBpTargetsPayload();
      if (changedPeriods.length === 0 && changedTargets.length === 0) {
        toast.info("No blood pressure changes to save");
        return;
      }
      const dataToSave = {};
      if (changedPeriods.length > 0) dataToSave.bloodPressureTimePeriod = changedPeriods;
      if (changedTargets.length > 0) dataToSave.bloodPressureTargetRange = changedTargets;

      let response;
      if (patientId) {
        response = await patientService.updateScheduleAndTargets(practiceId, patientId, dataToSave);
      } else {
        response = await practiceService.updateScheduleAndTargets(practiceId, dataToSave);
      }
      if (response.data?.success) {
        if (patientId) {
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
        }
        toast.success("Blood pressure settings saved successfully");
        // Refetch from server so React state + initialData reflect the actual
        // DB rows (and their auto-increment IDs). Untouched sections stay
        // visible because they're pulled back from the DB, not cleared.
        await reloadFromServer();
      } else {

        throw new Error(response.data?.message || "Save failed");
      }
    } catch (error) {
      console.error("Error saving blood pressure settings:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to save blood pressure settings");
    } finally {
      setSaving(prev => ({ ...prev, bp: false }));
    }
  };

  const saveBloodGlucose = async () => {
    if (isReadOnly) {
      toast.warning("Practice is locked. Schedule and targets are view-only.");
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    try {
      setSaving(prev => ({ ...prev, bg: true }));
      const changedPeriods = getChangedBgPeriodsPayload();
      const changedTargets = getChangedBgTargetsPayload();
      if (changedPeriods.length === 0 && changedTargets.length === 0) {
        toast.info("No blood glucose changes to save");
        return;
      }
      const dataToSave = {};
      if (changedPeriods.length > 0) dataToSave.bloodGlucoseTimePeriod = changedPeriods;
      if (changedTargets.length > 0) dataToSave.bloodGlucoseTargetRange = changedTargets;

      let response;
      if (patientId) {
        response = await patientService.updateScheduleAndTargets(practiceId, patientId, dataToSave);
      } else {
        response = await practiceService.updateScheduleAndTargets(practiceId, dataToSave);
      }
      if (response.data?.success) {
        if (patientId) {
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
        }
        toast.success("Blood glucose settings saved successfully");
        // Refetch from server so React state + initialData reflect the actual
        // DB rows (and their auto-increment IDs). Untouched sections stay
        // visible because they're pulled back from the DB, not cleared.
        await reloadFromServer();
      } else {

        throw new Error(response.data?.message || "Save failed");
      }
    } catch (error) {
      console.error("Error saving blood glucose settings:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to save blood glucose settings");
    } finally {
      setSaving(prev => ({ ...prev, bg: false }));
    }
  };

  const saveWeight = async () => {
    if (isReadOnly) {
      toast.warning("Practice is locked. Schedule and targets are view-only.");
      return;
    }
    if (!practiceId) {
      toast.error("Practice ID is required");
      return;
    }

    try {
      setSaving(prev => ({ ...prev, weight: true }));
      const changedPeriods = getChangedWeightPeriodsPayload();
      const changedTargets = getChangedWeightTargetsPayload();
      if (changedPeriods.length === 0 && !changedTargets) {
        toast.info("No weight changes to save");
        return;
      }
      const dataToSave = {};
      if (changedPeriods.length > 0) dataToSave.weightTimePeriod = changedPeriods;
      if (changedTargets) dataToSave.weightTargetRange = changedTargets;

      let response;
      if (patientId) {
        response = await patientService.updateScheduleAndTargets(practiceId, patientId, dataToSave);
      } else {
        response = await practiceService.updateScheduleAndTargets(practiceId, dataToSave);
      }
      if (response.data?.success) {
        if (patientId) {
          setUsePatientScheduleTargetsPreference(practiceId, patientId, true);
        }
        toast.success("Weight settings saved successfully");
        // Refetch from server so React state + initialData reflect the actual
        // DB rows (and their auto-increment IDs). Untouched sections stay
        // visible because they're pulled back from the DB, not cleared.
        await reloadFromServer();
      } else {

        throw new Error(response.data?.message || "Save failed");
      }
    } catch (error) {
      console.error("Error saving weight settings:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to save weight settings");
    } finally {
      setSaving(prev => ({ ...prev, weight: false }));
    }
  };

  // Reload the schedule/targets from the server and sync both the visible
  // React state and the initialData baseline. Called after every successful
  // save (so the UI always shows what's actually in the DB, including the
  // auto-increment row IDs assigned on INSERT) and from the Cancel button.
  //
  // This is critical on staging: previously we only updated `initialData`
  // from the locally-edited React state on save success, which kept the old
  // "React default" row IDs in state. On the next save those phantom IDs
  // would be sent back, miss the DB row, and the controller would INSERT
  // duplicate rows — making sections look "removed" / mis-aligned. Doing a
  // refetch closes that gap and also restores any other section the user
  // didn't touch (since the controller only updates sections present in the
  // payload, untouched data must come from the DB, not from stale state).
  const reloadFromServer = async () => {
    if (!practiceId) return;

    try {
      setLoading(true);
      let response;
      if (patientId) {
        response = await patientService.getScheduleAndTargets(practiceId, patientId);
      } else {
        response = await practiceService.getScheduleAndTargets(practiceId);
      }
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // Merge DB rows on top of the React-default rows (matching by name).
        // See the long comment in the initial-fetch useEffect for why this is
        // necessary — short version: the DB returns only the rows that have
        // been INSERTed, so a brand-new patient with one saved Bed Time would
        // otherwise collapse the visible list to 1 row.
        if (data.dailySchedule && data.dailySchedule.length > 0) {
          const dbByName = mapByName(data.dailySchedule);
          setDailySchedule(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              time: dbItem.time ? convertTimeTo12Hour(dbItem.time) : defaultItem.time
            };
          }));
        }
        if (data.bloodPressureTimePeriod && data.bloodPressureTimePeriod.length > 0) {
          const dbByName = mapByName(data.bloodPressureTimePeriod);
          setBpPeriods(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: BP_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          }));
        }
        if (data.bloodPressureTargetRange && data.bloodPressureTargetRange.length > 0) {
          const dbByName = mapByName(data.bloodPressureTargetRange);
          setBpTargets(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.label));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              sysMin: dbItem.systolicMin ?? dbItem.sysMin ?? defaultItem.sysMin,
              sysMax: dbItem.systolicMax ?? dbItem.sysMax ?? defaultItem.sysMax,
              diaMin: dbItem.diastolicMin ?? dbItem.diaMin ?? defaultItem.diaMin,
              diaMax: dbItem.diastolicMax ?? dbItem.diaMax ?? defaultItem.diaMax
            };
          }));
        }
        if (data.bloodGlucoseTimePeriod && data.bloodGlucoseTimePeriod.length > 0) {
          const dbByName = mapByName(data.bloodGlucoseTimePeriod);
          setBgPeriods(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: BG_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          }));
        }
        if (data.bloodGlucoseTargetRange && data.bloodGlucoseTargetRange.length > 0) {
          const dbByName = mapByName(data.bloodGlucoseTargetRange);
          setBgTargets(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.label));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              min: dbItem.minimum ?? dbItem.min ?? defaultItem.min,
              max: dbItem.maximum ?? dbItem.max ?? defaultItem.max
            };
          }));
        }
        if (data.weightTimePeriod && data.weightTimePeriod.length > 0) {
          const dbByName = mapByName(data.weightTimePeriod);
          setWeightPeriods(prev => prev.map((defaultItem) => {
            const dbItem = dbByName.get(normalizeName(defaultItem.name));
            if (!dbItem) return defaultItem;
            return {
              ...defaultItem,
              id: dbItem.id ?? defaultItem.id,
              start: WEIGHT_PERMANENT_START_TIMES[defaultItem.name] || (dbItem.startTime ? convertTimeTo12Hour(dbItem.startTime) : defaultItem.start),
              end: dbItem.endTime ? convertTimeTo12Hour(dbItem.endTime) : defaultItem.end,
              fixed: true
            };
          }));
        }
        if (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0) {
          setWeightTargets(prev => ({
            ...prev,
            weightMin: normalizeNumericValue(data.weightTargetRange.weightMin, prev.weightMin),
            weightMax: normalizeNumericValue(data.weightTargetRange.weightMax, prev.weightMax),
            bodyFatMin: normalizeNumericValue(data.weightTargetRange.bodyFatMin, prev.bodyFatMin),
            bodyFatMax: normalizeNumericValue(data.weightTargetRange.bodyFatMax, prev.bodyFatMax),
            bmiUnderweight: data.weightTargetRange.bmiUnderweight ?? prev.bmiUnderweight,
            bmiNormal: 18.5,
            bmiOverweight: 25,
            bmiObese: 30
          }));
        }

        // Rebuild the baselines via the same merge logic as initial fetch so
        // unsaved-rows aren't falsely treated as dirty after reload. See the
        // long comment in the initial-fetch useEffect for the full rationale.
        const buildMergedBaseline = (defaults, dbRows, mapDefaultToBaseline, mapDbToBaseline) => {
          const dbByName = mapByName(dbRows);
          return defaults.map((d) => {
            const dbItem = dbByName.get(normalizeName(d?.name ?? d?.label));
            return dbItem ? mapDbToBaseline(d, dbItem) : mapDefaultToBaseline(d);
          });
        };

        setInitialData({
          dailySchedule: buildMergedBaseline(
            dailySchedule,
            data.dailySchedule,
            (s) => ({ id: s.id, name: s.name, time: convertTimeTo24Hour(s.time) }),
            (s, db) => ({ id: db.id ?? s.id, name: db.name ?? s.name, time: db.time })
          ),
          bpPeriods: buildMergedBaseline(
            bpPeriods,
            data.bloodPressureTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          bpTargets: buildMergedBaseline(
            bpTargets,
            data.bloodPressureTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              systolicMin: normalizeNumericValue(t.sysMin, 0),
              systolicMax: normalizeNumericValue(t.sysMax, 0),
              diastolicMin: normalizeNumericValue(t.diaMin, 0),
              diastolicMax: normalizeNumericValue(t.diaMax, 0)
            }),
            (t, db) => ({
              id: db.id ?? t.id, name: db.name ?? t.label,
              systolicMin: normalizeNumericValue(db.systolicMin, normalizeNumericValue(t.sysMin, 0)),
              systolicMax: normalizeNumericValue(db.systolicMax, normalizeNumericValue(t.sysMax, 0)),
              diastolicMin: normalizeNumericValue(db.diastolicMin, normalizeNumericValue(t.diaMin, 0)),
              diastolicMax: normalizeNumericValue(db.diastolicMax, normalizeNumericValue(t.diaMax, 0))
            })
          ),
          bgPeriods: buildMergedBaseline(
            bgPeriods,
            data.bloodGlucoseTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          bgTargets: buildMergedBaseline(
            bgTargets,
            data.bloodGlucoseTargetRange,
            (t) => ({
              id: t.id, name: t.label,
              minimum: normalizeNumericValue(t.min, 0),
              maximum: normalizeNumericValue(t.max, 0)
            }),
            (t, db) => ({
              id: db.id ?? t.id, name: db.name ?? t.label,
              minimum: normalizeNumericValue(db.minimum, normalizeNumericValue(t.min, 0)),
              maximum: normalizeNumericValue(db.maximum, normalizeNumericValue(t.max, 0))
            })
          ),
          weightPeriods: buildMergedBaseline(
            weightPeriods,
            data.weightTimePeriod,
            (p) => ({
              id: p.id, name: p.name,
              startTime: convertTimeTo24Hour(p.start),
              endTime: convertTimeTo24Hour(p.end)
            }),
            (p, db) => ({
              id: db.id ?? p.id, name: db.name ?? p.name,
              startTime: db.startTime || convertTimeTo24Hour(p.start),
              endTime: db.endTime || convertTimeTo24Hour(p.end)
            })
          ),
          weightTargetRange: (data.weightTargetRange && Object.keys(data.weightTargetRange).length > 0)
            ? JSON.parse(JSON.stringify(data.weightTargetRange))
            : {
                weightMin: normalizeNumericValue(weightTargets.weightMin, 66),
                weightMax: normalizeNumericValue(weightTargets.weightMax, 220),
                bodyFatMin: normalizeNumericValue(weightTargets.bodyFatMin, 14),
                bodyFatMax: normalizeNumericValue(weightTargets.bodyFatMax, 27),
                bmiUnderweight: weightTargets.bmiUnderweight || "",
                bmiNormal: 18.5, bmiOverweight: 25, bmiObese: 30
              }
        });
      }
    } catch (error) {
      console.error("Error reloading schedule/targets from server:", error);
      toast.error("Failed to reload data from database");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCancel = async (section) => {
    await reloadFromServer();
  };

  // DIRTY CHECKS
  const isDailyDirty = () => {
    if (!initialData) return false;
    const current = dailySchedule.map(s => ({ ...s, time: convertTimeTo24Hour(s.time) }));
    const initial = initialData.dailySchedule.map(s => ({ 
      id: s.id, 
      name: s.name, 
      time: convertTimeTo24Hour(s.time)
    }));

    return JSON.stringify(current) !== JSON.stringify(initial);
  };

  const isBpDirty = () => {
    if (!initialData) return false;
    const currentPeriods = bpPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.bpPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));

    
    const currentTargets = bpTargets.map(t => ({
      id: t.id,
      name: t.label,
      systolicMin: normalizeNumericValue(t.sysMin, 0),
      systolicMax: normalizeNumericValue(t.sysMax, 0),
      diastolicMin: normalizeNumericValue(t.diaMin, 0),
      diastolicMax: normalizeNumericValue(t.diaMax, 0)
    }));
    const initialTargets = initialData.bpTargets.map(t => ({
      id: t.id,
      name: t.name,
      systolicMin: normalizeNumericValue(t.systolicMin ?? t.sysMin, 0),
      systolicMax: normalizeNumericValue(t.systolicMax ?? t.sysMax, 0),
      diastolicMin: normalizeNumericValue(t.diastolicMin ?? t.diaMin, 0),
      diastolicMax: normalizeNumericValue(t.diastolicMax ?? t.diaMax, 0)
    }));

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(initialTargets);
  };

  const isBgDirty = () => {
    if (!initialData) return false;
    const currentPeriods = bgPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.bgPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));

    
    const currentTargets = bgTargets.map(t => ({
      id: t.id,
      name: t.label,
      minimum: normalizeNumericValue(t.min, 0),
      maximum: normalizeNumericValue(t.max, 0)
    }));
    const initialTargets = initialData.bgTargets.map(t => ({
      id: t.id,
      name: t.name,
      minimum: normalizeNumericValue(t.minimum ?? t.min, 0),
      maximum: normalizeNumericValue(t.maximum ?? t.max, 0)
    }));

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(initialTargets);
  };

  const isWeightDirty = () => {
    if (!initialData) return false;
    const currentPeriods = weightPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      startTime: convertTimeTo24Hour(p.start), 
      endTime: convertTimeTo24Hour(p.end) 
    }));
    const initialPeriods = initialData.weightPeriods.map(p => ({ 
      id: p.id, 
      name: p.name, 
      startTime: convertTimeTo24Hour(p.startTime || p.start), 
      endTime: convertTimeTo24Hour(p.endTime || p.end) 
    }));


    const currentTargets = {
      weightMin: weightTargets.weightMin,
      weightMax: weightTargets.weightMax,
      bodyFatMin: weightTargets.bodyFatMin,
      bodyFatMax: weightTargets.bodyFatMax,
      bmiUnderweight: weightTargets.bmiUnderweight,
      bmiNormal: weightTargets.bmiNormal,
      bmiOverweight: weightTargets.bmiOverweight,
      bmiObese: weightTargets.bmiObese
    };
    const initialTargets = initialData.weightTargetRange;

    const normalizedInitial = {
      weightMin: normalizeNumericValue(initialTargets.weightMin, 66),
      weightMax: normalizeNumericValue(initialTargets.weightMax, 220),
      bodyFatMin: normalizeNumericValue(initialTargets.bodyFatMin, 14),
      bodyFatMax: normalizeNumericValue(initialTargets.bodyFatMax, 27),
      bmiUnderweight: initialTargets.bmiUnderweight || "",
      bmiNormal: 18.5,
      bmiOverweight: 25,
      bmiObese: 30
    };

    return JSON.stringify(currentPeriods) !== JSON.stringify(initialPeriods) || 
           JSON.stringify(currentTargets) !== JSON.stringify(normalizedInitial);
  };

  // Initial DB fetch: show spinner until real values load (avoid editing stale defaults).
  if (isInitialLoading) {
    return (
      <div
        className={`schedule-container${isReadOnly ? " schedule-container--readonly" : ""}`}
        aria-busy="true"
        aria-live="polite"
      >
        <PageLoadingSpinner minHeight="480px" />
      </div>
    );
  }

  return (
    <div className={`schedule-container${isReadOnly ? ' schedule-container--readonly' : ''}`}>
      {isReadOnly && (
        <div className="alert alert-warning py-2 mb-3" role="alert">
          This practice is locked. Schedule and target settings are view-only; saves are disabled.
        </div>
      )}

      {/* ===== DAILY SCHEDULE ===== */}
      <section className="section-card">
        <div className="section-header">
          <div className="section-header-title-wrap">
            <div className="section-header-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div>
              <h3 className="section-title">Daily Schedule</h3>
              <div className="section-subtitle">Configure daily routine time milestones</div>
            </div>
          </div>
        </div>
        <div className="section-body">
          <div className="daily-grid">
            {dailySchedule.map((item, i) => (
              <div key={item.id} className="daily-item">
                <label className="field-label">
                  {item.name} <span className="req">*</span>
                </label>
                <div className="time-wrapper">
                  <TimeInputAMPM
                    className="time-input daily-time-input"
                    value={convertTimeTo24Hour(item.time)}
                    onChange={(e) => updateDaily(i, convertTimeTo12Hour(e.target.value))}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('daily')} disabled={isReadOnly || isAnySaving || saving.daily || !isDailyDirty()}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancel
          </button>
          <button className="btn-save" onClick={saveDailySchedule} disabled={isReadOnly || isAnySaving || saving.daily || !isDailyDirty()}>
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            {saving.daily ? "Saving..." : "Save"}
          </button>
          {showSectionEventLogButtons && (
            <button className="btn-event" onClick={() => handleEventLog('dailySchedule')} disabled={isAnySaving}>
              <FontAwesomeIcon icon={faHistory} className="me-1" />
              Event Log
            </button>
          )}
        </div>
      </section>

      {/* ===== BLOOD PRESSURE TARGET SETTING ===== */}
      <section className="section-card">
        <div className="section-header">
          <div className="section-header-title-wrap">
            <div className="section-header-icon">
              <FontAwesomeIcon icon={faHeartbeat} />
            </div>
            <div>
              <h3 className="section-title">Blood Pressure Target Setting</h3>
              <div className="section-subtitle">Define blood pressure measurement periods and target ranges</div>
            </div>
          </div>
        </div>
        <div className="section-body">
          {/* Time Periods */}
          <div className="bp-periods-grid">
            {bpPeriods.map((p, i) => (
              <div key={p.id} className="bp-period-item">
                <label className="field-label">
                  {p.name} <span className="req">*</span>
                </label>
                <div className="range-time">
                  <div className="fixed-time-display">
                    {p.start}
                  </div>
                  <span className="dash">-</span>
                  <div className="time-wrapper">
                    <TimeInputAMPM
                      className="time-input"
                      value={convertTimeTo24Hour(p.end)}
                      onChange={(e) => updateBpPeriod(i, 'end', convertTimeTo12Hour(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Target Ranges */}
          <div className="bp-targets">
            {bpTargets.map((t, i) => (
              <div key={t.id} className="bp-target-card">
                <h5 className="target-title">
                  {t.label} <span className="req">*</span>
                </h5>
                <div className="bp-range-row">
                  <div className="bp-col">
                    <label>Systolic Pressure (mmHg)</label>
                    <div className="num-range">
                      <input
                        type="number"
                        value={t.sysMin}
                        onChange={(e) => updateBpTarget(i, 'sysMin', e.target.value)}
                        min="0"
                        max="300"
                      />
                      <span className="dash">-</span>
                      <input
                        type="number"
                        value={t.sysMax}
                        onChange={(e) => updateBpTarget(i, 'sysMax', e.target.value)}
                        min="0"
                        max="300"
                      />
                    </div>
                  </div>
                  <div className="bp-col">
                    <label>Diastolic Pressure (mmHg)</label>
                    <div className="num-range">
                      <input
                        type="number"
                        value={t.diaMin}
                        onChange={(e) => updateBpTarget(i, 'diaMin', e.target.value)}
                        min="0"
                        max="200"
                      />
                      <span className="dash">-</span>
                      <input
                        type="number"
                        value={t.diaMax}
                        onChange={(e) => updateBpTarget(i, 'diaMax', e.target.value)}
                        min="0"
                        max="200"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('bp')} disabled={isReadOnly || isAnySaving || saving.bp || !isBpDirty()}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancel
          </button>
          <button className="btn-save" onClick={saveBloodPressure} disabled={isReadOnly || isAnySaving || saving.bp || !isBpDirty()}>
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            {saving.bp ? "Saving..." : "Save"}
          </button>
          {showSectionEventLogButtons && (
            <button className="btn-event" onClick={() => handleEventLog('bloodPressure')} disabled={isAnySaving}>
              <FontAwesomeIcon icon={faHistory} className="me-1" />
              Event Log
            </button>
          )}
        </div>
      </section>

      {/* ===== BLOOD GLUCOSE TARGET SETTING ===== */}
      <section className="section-card">
        <div className="section-header">
          <div className="section-header-title-wrap">
            <div className="section-header-icon">
              <FontAwesomeIcon icon={faTint} />
            </div>
            <div>
              <h3 className="section-title">Blood Glucose Target Setting</h3>
              <div className="section-subtitle">Configure blood glucose targets for meal and sleep windows</div>
            </div>
          </div>
        </div>
        <div className="section-body">
          {/* Target Ranges and Time Ranges - 3 Column Layout */}
          <div className="bg-combined-grid">
            {/* Column 1: Wake-up Target, Bedtime Target, Before-Breakfast, After-Lunch, Bedtime */}
            <div className="bg-column">
              <div className="bg-target-item">
                <label className="field-label">
                  {bgTargets[0].label} <span className="req">*</span>
                </label>
                <div className="num-range">
                  <input
                    type="number"
                    value={bgTargets[0].min}
                    onChange={(e) => updateBgTarget(0, 'min', e.target.value)}
                    min="0"
                    max="300"
                  />
                  <span className="dash">-</span>
                  <input
                    type="number"
                    value={bgTargets[0].max}
                    onChange={(e) => updateBgTarget(0, 'max', e.target.value)}
                    min="0"
                    max="300"
                  />
                </div>
              </div>
              <div className="bg-target-item">
                <label className="field-label">
                  {bgTargets[3].label} <span className="req">*</span>
                </label>
                <div className="num-range">
                  <input
                    type="number"
                    value={bgTargets[3].min}
                    onChange={(e) => updateBgTarget(3, 'min', e.target.value)}
                    min="0"
                    max="300"
                  />
                  <span className="dash">-</span>
                  <input
                    type="number"
                    value={bgTargets[3].max}
                    onChange={(e) => updateBgTarget(3, 'max', e.target.value)}
                    min="0"
                    max="300"
                  />
                </div>
              </div>
              {/* Before-Breakfast (idx 1) */}
              {bgPeriods[1] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[1].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[1].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[1].end)}
                        onChange={(e) => updateBgPeriod(1, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* After-Lunch (idx 4) */}
              {bgPeriods[4] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[4].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[4].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[4].end)}
                        onChange={(e) => updateBgPeriod(4, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Bedtime (idx 7) */}
              {bgPeriods[7] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[7].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[7].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[7].end)}
                        onChange={(e) => updateBgPeriod(7, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Column 2: Before-Meal Target, Midnight Target, After-Breakfast, Before-Dinner, Midnight */}
            <div className="bg-column">
              <div className="bg-target-item">
                <label className="field-label">
                  {bgTargets[1].label} <span className="req">*</span>
                </label>
                <div className="num-range">
                  <input
                    type="number"
                    value={bgTargets[1].min}
                    onChange={(e) => updateBgTarget(1, 'min', e.target.value)}
                    min="0"
                    max="300"
                  />
                  <span className="dash">-</span>
                  <input
                    type="number"
                    value={bgTargets[1].max}
                    onChange={(e) => updateBgTarget(1, 'max', e.target.value)}
                    min="0"
                    max="300"
                  />
                </div>
              </div>
              <div className="bg-target-item">
                <label className="field-label">
                  {bgTargets[4].label} <span className="req">*</span>
                </label>
                <div className="num-range">
                  <input
                    type="number"
                    value={bgTargets[4].min}
                    onChange={(e) => updateBgTarget(4, 'min', e.target.value)}
                    min="0"
                    max="300"
                  />
                  <span className="dash">-</span>
                  <input
                    type="number"
                    value={bgTargets[4].max}
                    onChange={(e) => updateBgTarget(4, 'max', e.target.value)}
                    min="0"
                    max="300"
                  />
                </div>
              </div>
              {/* After-Breakfast (idx 2) */}
              {bgPeriods[2] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[2].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[2].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[2].end)}
                        onChange={(e) => updateBgPeriod(2, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Before-Dinner (idx 5) */}
              {bgPeriods[5] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[5].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[5].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[5].end)}
                        onChange={(e) => updateBgPeriod(5, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Midnight (idx 8) */}
              {bgPeriods[8] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[8].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[8].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[8].end)}
                        onChange={(e) => updateBgPeriod(8, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Column 3: After-Meal Target, Wake-up Time, Before-Lunch, After-Dinner */}
            <div className="bg-column">
              <div className="bg-target-item">
                <label className="field-label">
                  {bgTargets[2].label} <span className="req">*</span>
                </label>
                <div className="num-range">
                  <input
                    type="number"
                    value={bgTargets[2].min}
                    onChange={(e) => updateBgTarget(2, 'min', e.target.value)}
                    min="0"
                    max="300"
                  />
                  <span className="dash">-</span>
                  <input
                    type="number"
                    value={bgTargets[2].max}
                    onChange={(e) => updateBgTarget(2, 'max', e.target.value)}
                    min="0"
                    max="300"
                  />
                </div>
              </div>
              {/* Wake-up Time - find by name to ensure it's always visible */}
              {(() => {
                const wakeUpPeriod = bgPeriods.find(p => p.name === "Wake-up Time" || p.name === "Wake-up");
                if (!wakeUpPeriod) return null;
                const wakeUpIndex = bgPeriods.findIndex(p => p.name === "Wake-up Time" || p.name === "Wake-up");
                return (
                  <div className="bg-period-item">
                    <label className="field-label">
                      {wakeUpPeriod.name} <span className="req">*</span>
                    </label>
                    <div className="range-time">
                      <div className="fixed-time-display">
                        {wakeUpPeriod.start}
                      </div>
                      <span className="dash">-</span>
                      <div className="time-wrapper">
                        <TimeInputAMPM
                          className="time-input"
                          value={convertTimeTo24Hour(wakeUpPeriod.end)}
                          onChange={(e) => updateBgPeriod(wakeUpIndex, 'end', convertTimeTo12Hour(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Before-Lunch (idx 3) */}
              {bgPeriods[3] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[3].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[3].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[3].end)}
                        onChange={(e) => updateBgPeriod(3, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* After-Dinner (idx 6) */}
              {bgPeriods[6] && (
                <div className="bg-period-item">
                  <label className="field-label">
                    {bgPeriods[6].name} <span className="req">*</span>
                  </label>
                  <div className="range-time">
                    <div className="fixed-time-display">
                      {bgPeriods[6].start}
                    </div>
                    <span className="dash">-</span>
                    <div className="time-wrapper">
                      <TimeInputAMPM
                        className="time-input"
                        value={convertTimeTo24Hour(bgPeriods[6].end)}
                        onChange={(e) => updateBgPeriod(6, 'end', convertTimeTo12Hour(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('bg')} disabled={isReadOnly || isAnySaving || saving.bg || !isBgDirty()}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancel
          </button>
          <button className="btn-save" onClick={saveBloodGlucose} disabled={isReadOnly || isAnySaving || saving.bg || !isBgDirty()}>
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            {saving.bg ? "Saving..." : "Save"}
          </button>
          {showSectionEventLogButtons && (
            <button className="btn-event" onClick={() => handleEventLog('bloodGlucose')} disabled={isAnySaving}>
              <FontAwesomeIcon icon={faHistory} className="me-1" />
              Event Log
            </button>
          )}
        </div>
      </section>

      {/* ===== WEIGHT TARGET SETTING ===== */}
      <section className="section-card">
        <div className="section-header">
          <div className="section-header-title-wrap">
            <div className="section-header-icon">
              <FontAwesomeIcon icon={faWeight} />
            </div>
            <div>
              <h3 className="section-title">Weight Target Setting</h3>
              <div className="section-subtitle">Set recommended weight, body fat, and BMI target thresholds</div>
            </div>
          </div>
        </div>
        <div className="section-body">
          {/* Weight and Body Fat Ranges */}
          <div className="weight-targets-row">
            <div className="weight-target-item">
              <label className="field-label">
                Recommended Weight Range <span className="req">*</span>:
              </label>
              <div className="readonly-display">
                <span className="readonly-text">-- lb</span>
              </div>
            </div>
            <div className="weight-target-item">
              <label className="field-label">
                Recommended Body Fat Range <span className="req">*</span>:
              </label>
              <div className="readonly-display">
                <span className="readonly-text">-- %</span>
              </div>
            </div>
          </div>

          <div className="weight-targets-row">
            <div className="weight-target-item">
              <label className="field-label">
                Weight Target Range (lb) <span className="req">*</span>
              </label>
              <div className="num-range">
                <input
                  type="number"
                  value={weightTargets.weightMin}
                  onChange={(e) => updateWeightTarget('weightMin', e.target.value)}
                  min="0"
                  max="1000"
                />
                <span className="dash">-</span>
                <input
                  type="number"
                  value={weightTargets.weightMax}
                  onChange={(e) => updateWeightTarget('weightMax', e.target.value)}
                  min="0"
                  max="1000"
                />
              </div>
            </div>
            <div className="weight-target-item">
              <label className="field-label">
                Body Fat Target Range (%) <span className="req">*</span>
              </label>
              <div className="num-range">
                <input
                  type="number"
                  value={weightTargets.bodyFatMin}
                  onChange={(e) => updateWeightTarget('bodyFatMin', e.target.value)}
                  min="0"
                  max="100"
                />
                <span className="dash">-</span>
                <input
                  type="number"
                  value={weightTargets.bodyFatMax}
                  onChange={(e) => updateWeightTarget('bodyFatMax', e.target.value)}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>

          {/* BMI Target Range */}
          <div className="bmi-section">
            <label className="field-label" style={{ marginBottom: '12px' }}>
              BMI Target Range <span className="req">*</span>
            </label>
            <div className="bmi-grid">
              <div className="bmi-item">
                <label className="bmi-label">Underweight</label>
                <input
                  type="text"
                  className="bmi-input bmi-input-fixed-gray"
                  value="BMI"
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: 'none' }}
                />
              </div>
              <div className="bmi-item">
                <label className="bmi-label">Normal</label>
                <input
                  type="number"
                  className="bmi-input"
                  value={18.5}
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: 'none', background: '#fff' }}
                />
              </div>
              <div className="bmi-item">
                <label className="bmi-label">Overweight</label>
                <input
                  type="number"
                  className="bmi-input"
                  value={25}
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: 'none', background: '#fff' }}
                />
              </div>
              <div className="bmi-item">
                <label className="bmi-label">Obese</label>
                <input
                  type="number"
                  className="bmi-input"
                  value={30}
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: 'none', background: '#fff' }}
                />
              </div>
            </div>
          </div>

          {/* Time Ranges */}
          <div className="weight-periods-grid">
            {weightPeriods.map((p, i) => (
              <div key={p.id} className="weight-period-item">
                <label className="field-label">
                  {p.name} <span className="req">*</span>
                </label>
                <div className="range-time">
                  <div className="fixed-time-display">
                    {p.start}
                  </div>
                  <span className="dash">-</span>
                  <div className="time-wrapper">
                    <TimeInputAMPM
                      className="time-input"
                      value={convertTimeTo24Hour(p.end)}
                      onChange={(e) => updateWeightPeriod(i, 'end', convertTimeTo12Hour(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="btn-group">
          <button className="btn-cancel" onClick={() => handleCancel('weight')} disabled={isReadOnly || isAnySaving || saving.weight || !isWeightDirty()}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancel
          </button>
          <button className="btn-save" onClick={saveWeight} disabled={isReadOnly || isAnySaving || saving.weight || !isWeightDirty()}>
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            {saving.weight ? "Saving..." : "Save"}
          </button>
          {showSectionEventLogButtons && (
            <button className="btn-event" onClick={() => handleEventLog('weight')} disabled={isAnySaving}>
              <FontAwesomeIcon icon={faHistory} className="me-1" />
              Event Log
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default ScheduleTargetSettings;
